/**
 * Named filter sets for the library, in localStorage.
 *
 * Local rather than server-side on purpose: a saved filter is a browsing preference, not tenant
 * work product, and putting it in D1 would mean a table, a route and a tenant-isolation test for
 * something that is worth exactly as much as the browser it was saved in. If it later needs to
 * follow a user across devices, it moves — the shape here is already the payload.
 *
 * ponytail: localStorage, no sync. Revisit when someone asks for it on a second device.
 */
import type { MethodMode, SortOrder } from "@klapzo/kit-core";

const KEY = "klapzo.filtersets.v1";

/** How many sets one browser may keep. A guard against an unbounded localStorage key, not a product limit. */
const MAX_SETS = 20;

export interface FilterSet {
  name: string;
  query: string;
  mode: MethodMode | "";
  tags: string[];
  onlyFavourites: boolean;
  sort: SortOrder;
}

export function loadFilterSets(): FilterSet[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Hand-written localStorage and older shapes both land here, so every field is defaulted
    // rather than trusted — a malformed set must not take the library down.
    return parsed.flatMap((s) => {
      const o = s as Partial<FilterSet>;
      if (typeof o?.name !== "string" || !o.name) return [];
      return [{
        name: o.name,
        query: typeof o.query === "string" ? o.query : "",
        mode: o.mode === "online" || o.mode === "presencial" || o.mode === "hibrido" ? o.mode : "",
        tags: Array.isArray(o.tags) ? o.tags.filter((t): t is string => typeof t === "string") : [],
        onlyFavourites: o.onlyFavourites === true,
        sort: typeof o.sort === "string" ? (o.sort as SortOrder) : "title",
      }];
    });
  } catch {
    return [];
  }
}

function persist(sets: FilterSet[]): FilterSet[] {
  try {
    localStorage.setItem(KEY, JSON.stringify(sets));
  } catch {
    /* quota — the in-memory list is still correct for this session */
  }
  return sets;
}

/** Saving under an existing name REPLACES it: that is what "save" means when the name is the key. */
export function saveFilterSet(set: FilterSet): FilterSet[] {
  const rest = loadFilterSets().filter((s) => s.name !== set.name);
  return persist([set, ...rest].slice(0, MAX_SETS));
}

export function deleteFilterSet(name: string): FilterSet[] {
  return persist(loadFilterSets().filter((s) => s.name !== name));
}
